import { APP_BASE_HREF } from '@angular/common';
import { inject, Injectable, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService, PermissionService } from '@ipg/ngx-frontend-toolset';
import { filter, map, Observable, switchMap, take } from 'rxjs';
import { DialogService } from '../components/dialog/dialog.service';
import { permissionList } from '../models/permission.model';

/** A permission string as the backend names one in a 403, e.g. `tag:read`. */
const PERMISSION_PATTERN = /\b[a-z_]+:[a-z_]+\b/;

/** How long refusals are collected into a single notice, in milliseconds. */
const NOTICE_BATCH_MS = 300;

/** sessionStorage key: the permission the last sync reloaded for, and when. */
const LAST_SYNC_KEY = 'permission-sync:last';

/** How long after a sync the same permission does not sync again, in milliseconds. */
const RESYNC_AFTER_MS = 60_000;

/**
 * Service that handles permission changes triggered by either
 * a WebSocket event or a 403 INSUFFICIENT_PERMISSIONS response.
 *
 * A real change shows a dialog informing the user their permissions have
 * changed, then reloads to the app root so the route guards and sidebar
 * reflect the updated permissions.
 *
 * A 403 is only a change when the app believed the user held the permission
 * it names. Otherwise the page asked for something the user never had, and
 * blaming an administrator (then reloading into the same gap) only confused
 * people: that gets a notice naming the missing permission instead, and the
 * user stays on the page.
 */
@Injectable({ providedIn: 'root' })
export class PermissionSyncService {
  private readonly _dialog = inject(DialogService);
  private readonly _matDialog = inject(MatDialog);
  private readonly _auth = inject(AuthService);
  private readonly _baseHref = inject(APP_BASE_HREF);

  /*
    Resolved on first use, not injected: the first request of the app builds
    this service through the interceptor, and PermissionService pulls in the
    user fetch behind it — a request that would need this very service.
  */
  private readonly _injector = inject(Injector);

  private _syncing = false;

  /** Built once, on first use; see `_whenPermissionsLoaded`. */
  private _loadedPermissions$?: Observable<PermissionService>;

  /** Refused permissions waiting for the notice; null for an unnamed one. */
  private readonly _pending = new Set<string | null>();
  private _pendingWrite = false;
  private _noticeTimer: ReturnType<typeof setTimeout> | undefined;

  /** Reads already reported on the current page, and that page. */
  private readonly _reportedReads = new Set<string | null>();
  private _reportedPage = '';

  /**
   * Show a dialog and reload to the app root to reflect permission changes.
   * Debounced: if already syncing, subsequent calls are ignored.
   *
   * `reason` distinguishes between a role/permission change (default) and
   * a unit-tree change so the dialog text matches what actually happened.
   */
  syncPermissions(reason: 'role' | 'unit-tree' = 'role'): void {
    if (this._syncing) return;
    this._syncing = true;

    const message =
      reason === 'unit-tree'
        ? {
            title: 'Unit-structuur gewijzigd',
            message:
              'De unit-structuur is gewijzigd. De pagina wordt opnieuw geladen.',
          }
        : {
            title: 'Rechten gewijzigd',
            message:
              'Je rechten zijn gewijzigd door een beheerder. De pagina wordt vernieuwd.',
          };

    const dialogRef = this._dialog.alert(
      {
        ...message,
        confirmText: 'OK',
      },
      { disableClose: true }
    );

    dialogRef.afterClosed().subscribe(() => {
      // A full page reload (refreshing server state) to the base href root,
      // where defaultRedirectGuard picks a page the new permissions allow;
      // the current one may no longer be among them.
      location.replace(this._baseHref);
    });
  }

  /**
   * Handle a 403 INSUFFICIENT_PERMISSIONS response to a `method` request.
   *
   * Waits for the user's permissions to load, so a refusal arriving before
   * them is judged against what the user holds rather than an empty set.
   */
  handleInsufficientPermissions(method: string, errorMessage: unknown): void {
    const permission =
      typeof errorMessage === 'string'
        ? (PERMISSION_PATTERN.exec(errorMessage)?.[0] ?? null)
        : null;

    this._whenPermissionsLoaded().subscribe((permissions) => {
      if (
        permission !== null &&
        permissions.hasPermission(permission) &&
        !this._syncedRecentlyFor(permission)
      ) {
        // The app thinks the user holds it and the server disagrees: the
        // role changed since the user's permissions were loaded.
        this._rememberSync(permission);
        this.syncPermissions();
      } else {
        this._reportMissing(permission, method !== 'GET');
      }
    });
  }

  /**
   * Force logout the current user after being moved to a different unit
   * or when a support session is ended.
   *
   * Sessions are already invalidated server-side, so the user must
   * re-authenticate. Shows a dialog explaining the situation, then
   * redirects to login with a `reason` query parameter so the login
   * page can display a contextual message.
   */
  forceLogout(reason: 'moved' | 'support_session_ended'): void {
    if (this._syncing) return;
    this._syncing = true;

    // Immediately clear local auth tokens in background so a refresh won't stay authorized
    this._auth.authDB
      .pipe(switchMap((db) => db.delete('authTokens')))
      .subscribe({ error: () => {} });

    // Dismiss any existing open modals so the logout alert is front and center
    this._matDialog.closeAll();

    const message =
      reason === 'support_session_ended'
        ? {
            title: 'Support-sessie beëindigd',
            message:
              'De support-sessie voor deze klantomgeving is beëindigd. Je toegang is ingetrokken.',
            confirmText: 'Begrepen',
          }
        : {
            title: 'Verplaatst naar een andere unit',
            message:
              'Je account is verplaatst naar een andere unit. ' +
              'Je wordt uitgelogd en moet opnieuw inloggen.',
            confirmText: 'OK',
          };

    const dialogRef = this._dialog.alert(
      message,
      { disableClose: true },
    );

    dialogRef.afterClosed().subscribe(() => {
      const url =
        reason === 'support_session_ended'
          ? `${this._baseHref}login`
          : `${this._baseHref}login?reason=${reason}`;
      location.replace(url);
    });
  }

  /**
   * Tell the user that what they just tried needs `permissions`. For a page
   * that knows before asking the server, so the explanation reads the same
   * as the one a refused request gets.
   */
  explainMissing(permissions: string[]): void {
    this._openNotice(`Hiervoor ${missingSentence(permissions)}`);
  }

  /*
    A reload only settles a disagreement the server does not repeat. Scope
    checks read a role's permissions from a cache that can lag the database
    the user fetch reads (a backfill updates roles without clearing it), and
    then the reloaded page asks again, is refused again, and reloads again.
    So a refusal of the permission a sync reloaded for shortly before is
    reported as missing instead. In sessionStorage, to survive the reload.
  */
  private _syncedRecentlyFor(permission: string): boolean {
    try {
      const last = JSON.parse(sessionStorage.getItem(LAST_SYNC_KEY) ?? 'null');
      return last?.permission === permission && Date.now() - last.at < RESYNC_AFTER_MS;
    } catch {
      return false;
    }
  }

  private _rememberSync(permission: string): void {
    try {
      sessionStorage.setItem(LAST_SYNC_KEY, JSON.stringify({ permission, at: Date.now() }));
    } catch {
      // No sessionStorage: no guard, and a lagging server reloads more often.
    }
  }

  /** The PermissionService, as soon as the user's permissions have loaded. */
  private _whenPermissionsLoaded(): Observable<PermissionService> {
    if (!this._loadedPermissions$) {
      const permissions = this._injector.get(PermissionService);
      this._loadedPermissions$ = toObservable(permissions.isLoaded, {
        injector: this._injector,
      }).pipe(
        filter(Boolean),
        map(() => permissions)
      );
    }
    return this._loadedPermissions$.pipe(take(1));
  }

  /**
   * Queue a notice for a permission the user lacks; `write` marks a refused
   * change rather than a read.
   *
   * A page that loads several things it may not read refuses them in one
   * burst, so the refusals are collected into one notice. Reads are reported
   * once per page — loads repeat, polls even more so — while every refused
   * write is reported, since each one answers a click.
   */
  private _reportMissing(permission: string | null, write: boolean): void {
    if (!write) {
      if (location.pathname !== this._reportedPage) {
        this._reportedPage = location.pathname;
        this._reportedReads.clear();
      }
      if (this._reportedReads.has(permission)) return;
      this._reportedReads.add(permission);
    }

    this._pending.add(permission);
    this._pendingWrite ||= write;
    this._noticeTimer ??= setTimeout(() => this._showNotice(), NOTICE_BATCH_MS);
  }

  private _showNotice(): void {
    const named = [...this._pending].filter(
      (permission): permission is string => permission !== null
    );
    const intro = this._pendingWrite ? 'Dit is niet gelukt' : 'Niet alles kan worden getoond';

    this._pending.clear();
    this._pendingWrite = false;
    this._noticeTimer = undefined;
    if (this._syncing) return;

    this._openNotice(
      named.length === 0
        ? `${intro}: je hebt niet de rechten die daarvoor nodig zijn. ` +
            'Vraag een beheerder om je rol aan te passen.'
        : `${intro}: daarvoor ${missingSentence(named)}`
    );
  }

  private _openNotice(message: string): void {
    this._injector.get(MatSnackBar).open(message, 'OK', { duration: 10000 });
  }
}

/** The shared tail of every notice: "heb je het recht "X" nodig. Vraag …" */
function missingSentence(permissions: string[]): string {
  return permissions.length === 1
    ? `heb je het recht ${permissionList(permissions)} nodig. ` +
        'Vraag een beheerder om het aan je rol toe te voegen.'
    : `heb je de rechten ${permissionList(permissions)} nodig. ` +
        'Vraag een beheerder om ze aan je rol toe te voegen.';
}
